import {ReactComponent as IconChevron} from '#/components/icons/ico-chevron.svg';
import {ComponentProps, FunctionComponent, ReactNode} from 'react';
import {useToggle} from 'react-use';

export const ExpandableTile: FunctionComponent<
  {
    children: {content: ReactNode; expandedContentTitle: ReactNode; expandedContent: ReactNode};
    className?: string;
    onExpand?: () => void;
  } & Omit<ComponentProps<'div'>, 'children' | 'className'>
> = ({children, onExpand, className, ...rest}) => {
  const [isOpen, toggleIsOpen] = useToggle(false);

  const style = styles.default;

  const onExpandButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    toggleIsOpen();
    if (!isOpen && onExpand) {
      onExpand();
    }
  };

  return (
    <div
      {...rest}
      className={`w-full ${style.bgColor} rounded-2xl flex items-stretch ${style.divideColor} ${className}`}
    >
      <div className='grow w-full flex flex-col px-5 overflow-hidden'>
        {children.content}

        {isOpen && (
          <div className={`rounded-xl ${style.expandedBoxBgColor} px-4 py-3 mb-5 space-y-2.5`}>
            <span className={`${style.expandedBoxTitleColor} text-xs font-bold uppercase`}>
              {children.expandedContentTitle}
            </span>
            {children.expandedContent}
          </div>
        )}
      </div>

      <div className='w-16 py-5'>
        <button
          type='button'
          className='w-full h-full flex justify-center items-center hover:opacity-70 border-l border-gray-600'
          onClick={onExpandButtonClick}
        >
          <IconChevron className={`${style.arrowIconColor} ${isOpen ? '-scale-100' : ''}`} />
        </button>
      </div>
    </div>
  );
};

type ExpandableTileStyling = {
  bgColor: `bg-${string}`;
  divideColor: `divide-${string}`;
  expandedBoxBgColor: `bg-${string}`;
  expandedBoxTitleColor: `text-${string}`;
  arrowIconColor: `fill-${string} stroke-${string}`;
};

const defaultStyle: ExpandableTileStyling = {
  bgColor: 'bg-graybluedark2',
  divideColor: 'divide-gray-600',
  expandedBoxBgColor: 'bg-graybluedark3',
  expandedBoxTitleColor: 'text-[#618DFF]',
  arrowIconColor: 'fill-blue-600 stroke-blue-600',
};
const styles: Record<string, ExpandableTileStyling> = {
  default: defaultStyle,
};
